if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
          // Product was (re)injected or swapped: rebind the mobile scroll area. Idempotent.
          this._scheduleScrollSetup();
        });
        // --- Backstage-only state (no visual effect) ---
        this._panel = this.querySelector('.quick-add-modal__content');
        this._fetchCache = new Map(); // url -> Promise<string html> (bounded, FIFO)
        this._cacheLimit = 10;
        this._openToken = 0; // monotonically increasing, guards stale responses
        this._activeController = null; // AbortController for in-flight fetch
        this._activeOpener = null; // opener currently loading, for double-tap guard
        this._prefetchInFlight = new Set(); // urls currently being prefetched
        this._saveData =
          typeof navigator !== 'undefined' &&
          navigator.connection &&
          (navigator.connection.saveData || /2g/.test(navigator.connection.effectiveType || ''));

        // --- Mobile sheet: fixed header + scrollable variants + sticky buy bar + bottom fade ---
        this._mobileQuery = window.matchMedia('(max-width: 749px)'); // keep in sync with the CSS breakpoint
        this._reduceMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
        this._scroller = null; // .quick-add-scroll currently bound
        this._inner = null; // .quick-add-scroll-inner (the part that rubber-bands)
        this._scrollMax = null; // last max-height (px) written to the CSS variable
        this._scrollRaf = 0; // rAF handle for debounced refreshes
        this._fadeRaf = 0; // rAF handle for the scroll-driven fade update
        this._scrollResizeObserver = null;
        this._scrollMutationObserver = null;
        this._fade = null; // bottom fade element (lives inside the sticky bar)
        this._fadeRange = 48; // px of hidden content at which the fade reaches its full height
        this._fadeProgress = -1; // last value written, so we only touch the DOM when it changes
        this._gapFix = 0; // px the scroll area is pulled down onto the buy bar's divider (see _closeScrollGap)
        this._settleTimer = 0; // one last viewport measurement after Safari's bar has stopped moving
        this._fadeRgb = null; // last sampled sheet background
        this._skirt = null; // gradient strip BEHIND the browser's bottom bar
        this._barInset = 0; // last browser-bottom-bar inset (px) written to --quick-add-bar-inset
        this._barExtra = 0; // px the sheet is lifted BEYOND what the browser chrome measurably covers
        this._liftTrim = 0; // px. Raise it (e.g. 6) if the sheet still floats too high above the browser bar
        this._insetRaf = 0; // rAF handle for debounced inset updates
        this._insetListening = false;
        this._trackingTimer = 0; // removes the "tracking" class shortly after the bar stops moving
        this._probe = null; // invisible fixed element used to read the safe-area inset

        // --- Bottom gap: "More payment options" -> browser bar should equal buttons -> "More payment options" ---
        this._gapNet = null; // last resolved bottom space (px, signed), reused on the next open
        this._minTextClear = 2; // px: the link's text never gets closer than this to the sheet's bottom edge

        // Rubber band. The pull is never blocked: the further you drag past the edge, the more each
        // extra pixel costs. The offset itself is the state, and each finger delta is scaled by the local slope:
        //   slope = stiff * (1 - offset / limit) ^ power
        // Small pull = a little give. Near the limit the slope collapses, so extra dragging moves nothing.
        this._osLimit = 36; // asymptote in px: the content can never visually move more than this
        this._osStiff = 0.55; // resistance at the very start of the pull (bigger = heavier from the first pixel)
        this._osPower = 3; // how fast the resistance grows towards the limit (2 = old feel, higher = harder wall)
        this._osOmega = 10.5; // spring-back speed (rad/s). Lower = slower, softer return
        this._osY = 0; // current visual offset in px (signed). This is THE state.
        this._osLastY = 0; // last clientY of the finger that drives the pull
        this._osTouchId = null; // identifier of the finger that drives the pull (multi-touch safe)
        this._osActive = false;
        this._osRaf = 0; // rAF handle for the batched transform write while pulling
        this._osPending = false;
        this._osSpringRaf = 0; // rAF handle for the spring-back animation
        this._osNativeLocked = false; // true while the scroller is temporarily overflow:hidden (see _osGuardNative)

        // First element (inside .product__info-container) that belongs to the scrollable part.
        // NOTE: deliberately no generic "form" here, the instalments form lives in the header.
        this._scrollStartSelector = [
          '.qs-bundle-picker',
          'variant-selects',
          'quantity-input',
          '.product-form__quantity',
          '.product-form__input',
          'product-form',
          '.product-form',
        ].join(',');

        this._onScrollAreaScroll = () => {
          this._scheduleFadeUpdate();
          this._osGuardNative();
        };
        this._onScrollAreaChange = () => this._scheduleScrollRefresh();
        this._onInsetChange = () => this._scheduleInsetUpdate();
        this._onTouchStart = (e) => this._osStart(e);
        this._onTouchMove = (e) => this._osMove(e);
        this._onTouchEnd = (e) => this._osEnd(e);

        // --- Instalments line: reserve its exact height so it can't push anything when it loads ---
        this._termsHeights = {}; // viewport width -> measured height
        this._termsTimer = 0;
        this._termsWaitMs = 500; // max hold on the very first open; 0 = never wait
        this._termsGaveUp = false; // it never rendered (ineligible product): stop waiting this session
      }

      hide(preventFocus = false) {
        // Cancel any in-flight fetch so a late response can't do anything after close.
        this._cancelActiveFetch();
        this._activeOpener = null;
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        // Clear the injected product ONLY after the slide-out painted, so the panel never animates empty.
        const panel = this._panel || this.querySelector('.quick-add-modal__content');
        const clear = () => {
          if (!this.hasAttribute('open')) {
            this._teardownScrollArea();
            this.modalContent.innerHTML = '';
            this._fade = null; // it lived inside modalContent, it's gone now
          }
        };
        const onEnd = (e) => {
          if (e.target === panel && e.propertyName === 'transform') {
            clear();
            panel.removeEventListener('transitionend', onEnd);
          }
        };
        if (panel) {
          panel.addEventListener('transitionend', onEnd);
          setTimeout(() => {
            panel.removeEventListener('transitionend', onEnd);
            clear();
          }, 450);
        } else {
          clear();
        }
        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        // Ignore double-taps on the same opener while its own request is in flight.
        if (this._activeOpener === opener) return;
        // A tap on a different opener while one is loading supersedes it.
        this._cancelActiveFetch();
        const spinner = opener.querySelector && opener.querySelector('.loading__spinner');
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        if (spinner) spinner.classList.remove('hidden');
        const restoreOpener = () => {
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
        };
        const url = opener.getAttribute('data-product-url');
        const token = ++this._openToken;
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        this._activeController = controller;
        this._activeOpener = opener;
        this._getProductHTML(url, controller ? controller.signal : undefined)
          .then((responseText) => {
            if (token !== this._openToken) return; // a newer show() has started
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');
            if (!productElement || !this.modalContent) {
              restoreOpener();
              this._activeOpener = null;
              return;
            }
            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);
            // PaymentButton.init() affects layout height, so it must run BEFORE super.show().
            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            return this._waitForPaymentTerms().then(() => {
              if (token !== this._openToken) return;
              // Mobile only: build the scroll area + fade BEFORE the slide starts.
              this._startInsetTracking(); // measure the browser bar first so the very first frame is right
              this._setupScrollArea();
              this._ensureFade();
              this._ensureSkirt();
              void this.modalContent.offsetHeight; // settle layout pre-transition
              super.show(opener);
              this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();
              this._activeOpener = null;
              this._scheduleScrollRefresh(); // safety net once open
              this._watchPaymentTerms(); // remember the instalments height for next time
              this._runIdle(() => {
                if (window.ProductModel) window.ProductModel.loadShopifyXR();
              });
            });
          })
          .catch((error) => {
            if (token !== this._openToken) return; // superseded, ignore silently
            if (error && error.name === 'AbortError') return; // cancelled on purpose
            this._activeOpener = null;
          })
          .finally(() => {
            if (token === this._openToken) restoreOpener();
          });
      }

      // --- Fetch / cache helpers (backstage only) ---
      _cancelActiveFetch() {
        if (this._activeController) {
          this._activeController.abort();
          this._activeController = null;
        }
      }
      _getProductHTML(url, signal) {
        const cached = this._fetchCache.get(url);
        if (cached) {
          this._fetchCache.delete(url);
          this._fetchCache.set(url, cached);
          return cached;
        }
        const request = fetch(url, signal ? { signal } : undefined)
          .then((response) => response.text())
          .catch((error) => {
            this._fetchCache.delete(url);
            throw error;
          });
        this._setCache(url, request);
        return request;
      }
      _setCache(url, promise) {
        this._fetchCache.set(url, promise);
        while (this._fetchCache.size > this._cacheLimit) {
          const oldestKey = this._fetchCache.keys().next().value;
          this._fetchCache.delete(oldestKey);
        }
      }
      _runIdle(callback) {
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(callback, { timeout: 200 });
        } else {
          window.requestAnimationFrame(() => window.requestAnimationFrame(callback));
        }
      }

      // --- Intent prefetch ---
      prefetchProduct(url) {
        if (!url) return;
        if (this._saveData) return; // respect save-data / slow connections
        if (this._fetchCache.has(url)) return; // already cached
        if (this._prefetchInFlight.has(url)) return; // one in-flight per URL
        this._prefetchInFlight.add(url);
        const request = fetch(url, { priority: 'low' })
          .then((response) => response.text())
          .catch((error) => {
            this._fetchCache.delete(url);
            throw error;
          })
          .finally(() => {
            this._prefetchInFlight.delete(url);
          });
        this._setCache(url, request);
        request.catch(() => {});
      }

      // --- Mobile sheet: fixed header, scrollable variants, sticky buy bar, bottom fade ---
      //
      // Layout on mobile (<750px):
      //   [ image / title / price / tax / instalments ]        <- never moves
      //   ------------------ divider (static) ---------------
      //   [ .quick-add-scroll                                  <- scroll box, never transformed
      //       [ .quick-add-scroll-inner ]: variants, quantity  <- only this rubber-bands
      //   ]
      //   ------------------ divider (static) ---------------  <- top edge of the sticky bar (CSS ::before)
      //   [ .quick-add-sticky-bar: Add to cart | Apple Pay ]   <- side by side, above the browser bar
      //   [ .quick-add-skirt, child of <quick-add-modal> ]     <- plain gradient behind the browser bar
      //
      // Wraps everything from the variant picker down into .quick-add-scroll > .quick-add-scroll-inner.
      // Runs on the parsed (detached) element, so nothing is moved on the live page.
      wrapScrollArea(productElement) {
        if (!this._mobileQuery.matches) return; // desktop drawer keeps its original DOM
        const container = productElement.querySelector('.product__info-container');
        if (!container || container.querySelector(':scope > .quick-add-scroll')) return;
        const firstMatch = container.querySelector(this._scrollStartSelector);
        if (!firstMatch) return;
        let start = firstMatch;
        while (start && start.parentElement !== container) start = start.parentElement;
        if (!start) return;
        const doc = productElement.ownerDocument || document;
        const scroller = doc.createElement('div');
        scroller.className = 'quick-add-scroll';
        const inner = doc.createElement('div');
        inner.className = 'quick-add-scroll-inner';
        scroller.appendChild(inner);
        container.insertBefore(scroller, start);
        // The buy form must stay OUTSIDE the scroller: a sticky element inside a scroller only sticks
        // to the scroller's edge, so it would scroll away with the variants.
        const isBuyForm = (n) =>
          n.nodeType === 1 &&
          (n.matches('product-form, .product-form') || n.querySelector('product-form, .product-form'));
        let node = start;
        while (node) {
          const next = node.nextSibling;
          if (isBuyForm(node)) break;
          inner.appendChild(node);
          node = next;
        }
      }

      // Groups Add to cart + dynamic checkout into one sticky bar WITHOUT lifting them out of the
      // <form>, so submit / Apple Pay keep working exactly as before.
      wrapStickyBar(productElement) {
        if (!this._mobileQuery.matches) return;
        const buttons = productElement.querySelector('.product-form__buttons');
        if (!buttons) return;
        if (buttons.parentElement && buttons.parentElement.classList.contains('quick-add-sticky-bar')) return;
        const bar = (productElement.ownerDocument || document).createElement('div');
        bar.className = 'quick-add-sticky-bar';
        buttons.parentNode.insertBefore(bar, buttons);
        bar.appendChild(buttons);
        const pay = productElement.querySelector('.shopify-payment-button');
        if (pay && !bar.contains(pay) && bar.parentElement && bar.parentElement.contains(pay)) {
          bar.appendChild(pay);
        }
        // Dawn's generic ".product__info-container > *" rule puts 1.6rem of margin above whatever wraps the buy
        // form. That left a strip between the scroll area and the bar's divider, so the content was cut 1.6rem
        // ABOVE the divider and the fade (a mask on the scroll area) ended there too. Tag every wrapper between
        // the bar and the container so the CSS can zero them out.
        const container = productElement.querySelector('.product__info-container');
        for (let node = bar.parentElement; container && node && node !== container && container.contains(node); node = node.parentElement) {
          node.classList.add('quick-add-buy-wrap');
        }
      }

      _getPanel() {
        if (!this._panel) this._panel = this.querySelector('.quick-add-modal__content');
        return this._panel;
      }

      // The fade is a mask on .quick-add-scroll (pure CSS), so there is nothing to build.
      _ensureFade() {}

      // The strip BEHIND the browser's bottom bar. A sibling of the sheet, not a child: the sheet has
      // overflow:hidden + a transform, which would clip it or trap its position.
      // Survives open/close (only the injected product is cleared), so it is built once.
      _ensureSkirt() {
        if (!this._mobileQuery.matches) return;
        if (this._skirt && this._skirt.parentNode === this) return;
        const skirt = document.createElement('div');
        skirt.className = 'quick-add-skirt';
        skirt.setAttribute('aria-hidden', 'true');
        this.appendChild(skirt);
        this._skirt = skirt;
      }

      // --- Browser bottom-bar tracking (Safari, Chrome, Firefox, in-app browsers) ---
      //
      // The browser's bottom bar changes size (expanded / minimized / hidden) and on some versions
      // floats OVER the page. CSS cannot know how tall it is right now, so it is measured and published
      // as one CSS variable:
      //
      //   --quick-add-bar-inset : px at the bottom of the layout viewport covered by browser chrome or
      //                           the home indicator (never less than the safe area).
      //
      //   inset = max( safe-area , innerHeight - (visualViewport.height + visualViewport.offsetTop) )
      //
      // The CSS lifts the sheet with translate3d(0, -inset, 0): compositor only, no layout.
      // While the bar is moving the sheet gets a short "tracking" transition so it glides with the bar
      // instead of stepping; when the bar settles, the class is removed again.
      //
      // _barExtra is how much of that lift goes BEYOND what the chrome measurably covers (safe-area larger
      // than the covered area, typically when the bar is minimized). _balanceBottomGap() subtracts it from
      // the in-sheet padding, so the visible gap to the bar stays constant in every bar state.
      _ensureProbe() {
        if (this._probe && this._probe.parentNode === this) return this._probe;
        const probe = document.createElement('div');
        probe.setAttribute('aria-hidden', 'true');
        // Reads the real safe-area inset (env() cannot be read from JS directly, but a padding can).
        probe.style.cssText =
          'position:fixed;left:0;top:0;width:1px;height:1px;padding-bottom:env(safe-area-inset-bottom,0px);' +
          'box-sizing:content-box;visibility:hidden;pointer-events:none;';
        this.appendChild(probe);
        this._probe = probe;
        return probe;
      }
      _startInsetTracking() {
        if (!this._mobileQuery.matches) return;
        this._updateBarInset(true);
        if (this._insetListening) return;
        this._insetListening = true;
        window.addEventListener('resize', this._onInsetChange, { passive: true });
        window.addEventListener('orientationchange', this._onInsetChange, { passive: true });
        if (window.visualViewport) {
          window.visualViewport.addEventListener('resize', this._onInsetChange, { passive: true });
          // scroll fires when the visual viewport moves, which is what the bar collapsing does
          window.visualViewport.addEventListener('scroll', this._onInsetChange, { passive: true });
        }
      }
      _stopInsetTracking() {
        if (this._insetRaf) {
          window.cancelAnimationFrame(this._insetRaf);
          this._insetRaf = 0;
        }
        if (this._trackingTimer) {
          window.clearTimeout(this._trackingTimer);
          this._trackingTimer = 0;
        }
        if (this._settleTimer) {
          window.clearTimeout(this._settleTimer);
          this._settleTimer = 0;
        }
        const panel = this._getPanel();
        if (panel) panel.classList.remove('quick-add-tracking');
        if (!this._insetListening) return;
        this._insetListening = false;
        window.removeEventListener('resize', this._onInsetChange);
        window.removeEventListener('orientationchange', this._onInsetChange);
        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', this._onInsetChange);
          window.visualViewport.removeEventListener('scroll', this._onInsetChange);
        }
      }
      _scheduleInsetUpdate() {
        // Safari animates its bottom bar and reports the final viewport late. One more pass once the events stop,
        // otherwise the scroll area and the fade can stay sized for a half-way state until something else moves.
        if (this._settleTimer) window.clearTimeout(this._settleTimer);
        this._settleTimer = window.setTimeout(() => {
          this._settleTimer = 0;
          this._updateBarInset(false);
          this._scheduleScrollRefresh();
        }, 250);
        if (this._insetRaf) return;
        this._insetRaf = window.requestAnimationFrame(() => {
          this._insetRaf = 0;
          this._updateBarInset(false);
        });
      }
      _updateBarInset(initial) {
        const panel = this._getPanel();
        if (!panel) return;
        let inset = 0;
        let extra = 0;
        if (this._mobileQuery.matches) {
          const probe = this._ensureProbe();
          const safe = parseFloat(window.getComputedStyle(probe).paddingBottom) || 0;
          // The sheet is position:fixed, anchored to the LAYOUT viewport (window.innerHeight).
          const layoutHeight = window.innerHeight;
          const vv = window.visualViewport;
          const visible = vv ? vv.height + vv.offsetTop : layoutHeight;
          // How much of the bottom is covered by browser chrome floating OVER the page.
          const covered = Math.max(0, layoutHeight - visible);
          inset = Math.max(safe, covered);
          // Guard against absurd values (keyboard open, split view): a bar is never taller than ~14rem.
          if (inset > 220) inset = safe;
          inset = Math.max(0, inset - this._liftTrim);
          // The part of the lift that is NOT explained by measurable chrome
          extra = Math.max(0, inset - covered);
        }
        // Whole pixels: sub-pixel churn from the bar animating would otherwise cause needless writes
        inset = Math.round(inset);
        extra = Math.round(extra);
        const insetChanged = inset !== this._barInset;
        const extraChanged = extra !== this._barExtra;
        if (!insetChanged && !extraChanged) {
          // Same lift, but the viewport itself moved (dvh follows Safari's bar, so the sheet's max-height can
          // change with no change in the inset). Re-measure anyway, or the scroll area and fade go stale.
          if (!initial) this._scheduleScrollRefresh();
          return;
        }
        this._barExtra = extra;
        if (!insetChanged) {
          // Same lift, but the bottom gap depends on _barExtra: re-balance it
          this._scheduleScrollRefresh();
          return;
        }
        this._barInset = inset;
        // ONE write on the shared parent: the sheet and the skirt both inherit it
        this.style.setProperty('--quick-add-bar-inset', `${inset}px`);
        if (!initial) {
          // Glide with the bar while it moves, then hand the easing back
          panel.classList.add('quick-add-tracking');
          if (this._trackingTimer) window.clearTimeout(this._trackingTimer);
          this._trackingTimer = window.setTimeout(() => {
            this._trackingTimer = 0;
            panel.classList.remove('quick-add-tracking');
          }, 220);
        }
        // the sheet's max-height changed, so the scroll area must be re-measured
        this._scheduleScrollRefresh();
      }

      _scheduleScrollSetup() {
        window.requestAnimationFrame(() => this._setupScrollArea());
      }
      _scheduleScrollRefresh() {
        if (this._scrollRaf) return;
        this._scrollRaf = window.requestAnimationFrame(() => {
          this._scrollRaf = 0;
          this._refreshScrollArea();
        });
      }

      // Idempotent: binds to the current .quick-add-scroll, or does nothing new if already bound.
      _setupScrollArea() {
        const panel = this._getPanel();
        const scroller = this.modalContent ? this.modalContent.querySelector('.quick-add-scroll') : null;
        if (scroller && scroller === this._scroller) {
          this._scheduleScrollRefresh();
          return;
        }
        // Rebinding must NOT stop the viewport tracking: show() starts it right before calling this, and
        // stopping it here left the sheet deaf to Safari's bar after the first open (the scroll area and the
        // fade then stayed sized for whatever state the bar was in when the sheet opened).
        this._teardownScrollArea({ keepTracking: true });
        if (!scroller || !panel) return;
        this._scroller = scroller;
        this._inner = scroller.querySelector('.quick-add-scroll-inner');
        this.modalContent.classList.add('quick-add-has-scroll');
        scroller.addEventListener('scroll', this._onScrollAreaScroll, { passive: true });
        // Rubber band (touchmove needs passive:false to preventDefault while we are pulling past an edge)
        scroller.addEventListener('touchstart', this._onTouchStart, { passive: true });
        scroller.addEventListener('touchmove', this._onTouchMove, { passive: false });
        scroller.addEventListener('touchend', this._onTouchEnd, { passive: true });
        scroller.addEventListener('touchcancel', this._onTouchEnd, { passive: true });
        // ONE ResizeObserver, on the few things that can change heights (Shop Pay terms loading,
        // variant change, Apple Pay button rendering late, rotation...). The MutationObserver that used
        // to watch the whole subtree is gone: the ResizeObserver already fires for real size changes,
        // and the subtree observer was pure overhead on low-end phones.
        if (typeof ResizeObserver !== 'undefined') {
          this._scrollResizeObserver = new ResizeObserver(this._onScrollAreaChange);
          this._scrollResizeObserver.observe(this.modalContent);
          this._scrollResizeObserver.observe(scroller);
          if (this._inner) this._scrollResizeObserver.observe(this._inner);
          const bar = this.modalContent.querySelector('.quick-add-sticky-bar');
          if (bar) this._scrollResizeObserver.observe(bar);
        }
        this._refreshScrollArea();
      }

      _teardownScrollArea({ keepTracking = false } = {}) {
        this._stopWatchingPaymentTerms();
        if (!keepTracking) this._stopInsetTracking();
        if (this._osRaf) {
          window.cancelAnimationFrame(this._osRaf);
          this._osRaf = 0;
          this._osPending = false;
        }
        this._osStopSpring();
        this._osUnlockNative(); // needs this._scroller, so it must run before it is cleared below
        if (this._fadeRaf) {
          window.cancelAnimationFrame(this._fadeRaf);
          this._fadeRaf = 0;
        }
        if (this._scroller) {
          this._scroller.removeEventListener('scroll', this._onScrollAreaScroll);
          this._scroller.removeEventListener('touchstart', this._onTouchStart);
          this._scroller.removeEventListener('touchmove', this._onTouchMove);
          this._scroller.removeEventListener('touchend', this._onTouchEnd);
          this._scroller.removeEventListener('touchcancel', this._onTouchEnd);
        }
        if (this._inner) {
          this._inner.style.transform = '';
          this._inner.style.willChange = '';
        }
        if (this._scrollResizeObserver) {
          this._scrollResizeObserver.disconnect();
          this._scrollResizeObserver = null;
        }
        if (this._scrollMutationObserver) {
          this._scrollMutationObserver.disconnect();
          this._scrollMutationObserver = null;
        }
        if (this._scrollRaf) {
          window.cancelAnimationFrame(this._scrollRaf);
          this._scrollRaf = 0;
        }
        this._scroller = null;
        this._inner = null;
        this._scrollMax = null;
        this._fadeProgress = -1;
        this._osY = 0;
        this._osActive = false;
        this._osTouchId = null;
        this._gapFix = 0;
        if (!keepTracking) {
          this._barInset = 0;
          this._barExtra = 0;
          this.style.removeProperty('--quick-add-bar-inset');
        }
        if (this.modalContent) {
          this.modalContent.classList.remove('quick-add-has-scroll');
          this.modalContent.style.removeProperty('--quick-add-scroll-max');
        }
        const panel = this._getPanel();
        if (panel && !keepTracking) panel.classList.remove('quick-add-tracking');
        // the fade progress now lives on the scroll area itself (discarded with it); this clears the old location
        this.style.removeProperty('--quick-add-fade-progress');
      }

      _refreshScrollArea() {
        this._syncFadeColor();
        this._ensureFade();
        this._closeScrollGap();
        this._measureScrollArea();
        // The bottom gap changes the bar's height, so the scroll area has to be fitted again afterwards
        if (this._balanceBottomGap()) this._measureScrollArea();
        this._updateScrollFade();
        this._centerMoreOptions();
      }

      // Centres "More payment options" on the buy bar. CSS already shifts it into place for the normal markup;
      // this measures where the text REALLY is and adds whatever correction is still missing, so it is centred
      // even if Shopify renders that link somewhere unexpected. Setting a transform never changes layout, so it
      // cannot retrigger the ResizeObserver: no loop.
      _centerMoreOptions() {
        if (!this._mobileQuery.matches || !this.modalContent) return;
        const bar = this.modalContent.querySelector('.quick-add-sticky-bar');
        const link = bar && bar.querySelector('.shopify-payment-button__more-options');
        if (!link) return;
        const cs = window.getComputedStyle(link);
        if (cs.display === 'none' || cs.visibility === 'hidden') return;
        const range = document.createRange();
        range.selectNodeContents(link);
        const text = range.getBoundingClientRect();
        if (!text.width) return;
        const box = bar.getBoundingClientRect();
        const bs = window.getComputedStyle(bar);
        const target = (box.left + (parseFloat(bs.paddingLeft) || 0) + box.right - (parseFloat(bs.paddingRight) || 0)) / 2;
        const delta = target - (text.left + text.width / 2);
        if (Math.abs(delta) < 0.5) return;
        let current = 0;
        if (cs.transform && cs.transform !== 'none') {
          try {
            current = new DOMMatrixReadOnly(cs.transform).m41 || 0;
          } catch (e) {
            current = 0;
          }
        }
        link.style.setProperty('--quick-add-more-dx', `${(current + delta).toFixed(2)}px`);
      }

      // --- Bottom gap: "More payment options" -> browser bar ---
      //
      // Goal: gap(text -> browser bar) == gap(Apple Pay / Add to cart buttons -> text), always.
      //
      //   reference = text.top - (bottom of the buttons above it)        <- the gap the design wants everywhere
      //   current   = sheet.bottom - text.bottom                         <- what is in the sheet right now
      //   wanted    = reference - _barExtra                              <- minus lift that is not covered by chrome
      //
      // The difference is written to the sticky bar as padding (or, if even zero padding is too much, as a
      // negative bottom margin). Everything is measured from real rendered rectangles, so it does not depend on
      // font metrics, Shopify's button markup or whatever else may sit under the link. Because it re-runs on
      // every refresh (bar expanding/collapsing, rotation, late-loading payment buttons), the gap stays constant.
      _balanceBottomGap() {
        const panel = this._getPanel();
        const bar = this.modalContent && this.modalContent.querySelector('.quick-add-sticky-bar');
        if (!panel || !bar || !this._mobileQuery.matches) return false;
        const link = bar.querySelector('.shopify-payment-button__more-options');
        if (!link) return this._clearBottomSpace(bar); // no link: the CSS default applies
        const ls = window.getComputedStyle(link);
        if (ls.display === 'none' || ls.visibility === 'hidden') return this._clearBottomSpace(bar);
        const range = document.createRange();
        range.selectNodeContents(link);
        const text = range.getBoundingClientRect();
        if (!text.height) return false;
        // Lowest edge of the buttons sitting above the text
        let above = -Infinity;
        bar.querySelectorAll('.product-form__submit, .shopify-payment-button__button').forEach((el) => {
          const cs = window.getComputedStyle(el);
          if (cs.display === 'none' || cs.visibility === 'hidden') return;
          const r = el.getBoundingClientRect();
          if (r.height > 0 && r.bottom <= text.top + 1) above = Math.max(above, r.bottom);
        });
        if (!isFinite(above)) return false;
        const reference = text.top - above;
        const current = panel.getBoundingClientRect().bottom - text.bottom + this._barInset - this._barExtra;
        const wanted = Math.max(this._minTextClear, reference - this._barExtra);
        const delta = wanted - current;
        if (Math.abs(delta) < 0.5) return false;
        const bs = window.getComputedStyle(bar);
        // net space under the link = padding minus any negative margin currently applied
        const net = (parseFloat(bs.paddingBottom) || 0) + Math.min(0, parseFloat(bs.marginBottom) || 0);
        let next = net + delta;
        if (!isFinite(next)) return false;
        next = Math.max(-60, Math.min(80, Math.round(next * 100) / 100));
        this._gapNet = next;
        this._applyBottomSpace(bar, next);
        return true;
      }
      _applyBottomSpace(bar, net) {
        bar.style.setProperty('--quick-add-bottom-gap', `${Math.max(0, net).toFixed(2)}px`);
        bar.style.setProperty('--quick-add-bottom-pull', `${Math.max(0, -net).toFixed(2)}px`);
      }
      _clearBottomSpace(bar) {
        if (!bar.style.getPropertyValue('--quick-add-bottom-gap') && !bar.style.getPropertyValue('--quick-add-bottom-pull')) {
          return false;
        }
        bar.style.removeProperty('--quick-add-bottom-gap');
        bar.style.removeProperty('--quick-add-bottom-pull');
        return true;
      }
      // Runs on the detached product element: pre-sets the remembered bottom space so the very first frame is right.
      reserveBottomGap(productElement) {
        if (!this._mobileQuery.matches || this._gapNet === null) return;
        const bar = productElement.querySelector('.quick-add-sticky-bar');
        if (!bar || !bar.querySelector('.shopify-payment-button__more-options')) return;
        this._applyBottomSpace(bar, this._gapNet);
      }

      // Sample the colour that ACTUALLY paints behind the sheet content: walk UP from the scroll area
      // and take the first opaque background. Feeds both the fade and the buy bar, so both always match
      // what's behind them, in light and dark mode. Only runs on refresh, never during a scroll.
      _syncFadeColor() {
        const start = this._scroller || (this.modalContent && this.modalContent.firstElementChild);
        if (!start) return;
        let el = start;
        let rgb = null;
        while (el) {
          // never sample our own generated chrome
          if (!el.classList || (!el.classList.contains('quick-add-sticky-bar') && !el.classList.contains('quick-add-fade'))) {
            const bg = window.getComputedStyle(el).backgroundColor;
            const m = bg && bg.match(/rgba?\(([^)]+)\)/);
            if (m) {
              const p = m[1].split(',').map((s) => parseFloat(s));
              const a = p.length > 3 ? p[3] : 1;
              if (a > 0.5 && p.length >= 3 && !(p[0] === 0 && p[1] === 0 && p[2] === 0 && a < 1)) {
                rgb = `${Math.round(p[0])}, ${Math.round(p[1])}, ${Math.round(p[2])}`;
                break;
              }
            }
          }
          el = el.parentElement;
        }
        if (rgb && rgb !== this._fadeRgb) {
          this._fadeRgb = rgb;
          // ONE write on the shared parent: the sheet, the fade and the skirt all inherit it
          this.style.setProperty('--quick-add-fade-rgb', rgb);
        }
      }

      // max-height of the scroll area = sheet max-height - everything above it - the buy bar below it.
      // Measured by LAYOUT size (offsetTop / offsetHeight), which ignores the sheet's transform.
      _measureScrollArea() {
        const scroller = this._scroller;
        const panel = this._getPanel();
        if (!scroller || !panel || !this.modalContent) return;
        if (!this._mobileQuery.matches) {
          // Rotated / resized into the desktop layout: let the CSS take over.
          this.modalContent.style.removeProperty('--quick-add-scroll-max');
          this._scrollMax = null;
          return;
        }
        let cap = parseFloat(window.getComputedStyle(panel).maxHeight);
        if (!Number.isFinite(cap)) cap = window.innerHeight * 0.8;
        // Distance from the top of the panel to the top of the scroll area, in layout px.
        let offset = 0;
        let node = scroller;
        while (node && node !== panel) {
          offset += node.offsetTop || 0;
          node = node.offsetParent;
        }
        if (node !== panel) {
          // offsetParent chain did not reach the panel (unusual layout): fall back to rects.
          offset = scroller.getBoundingClientRect().top - panel.getBoundingClientRect().top;
        }
        if (!(offset > 0)) return; // not laid out yet, keep the CSS fallback
        // Everything BELOW the scroll area (the buy bar / form) eats into the same cap.
        const contentEnd = this.modalContent.scrollHeight;
        const scrollerBottom = offset + scroller.offsetHeight;
        const below = Math.max(0, contentEnd - scrollerBottom);
        const max = Math.max(120, Math.floor(cap - offset - below - 2));
        if (max !== this._scrollMax) {
          this._scrollMax = max;
          this.modalContent.style.setProperty('--quick-add-scroll-max', `${max}px`);
        }
      }

      // Scroll events can fire faster than frames: coalesce to one update per frame.
      _scheduleFadeUpdate() {
        if (this._fadeRaf) return;
        this._fadeRaf = window.requestAnimationFrame(() => {
          this._fadeRaf = 0;
          this._updateScrollFade();
        });
      }

      // The fade is a mask on the scroll area, so it can only sit ON the divider if the scroll area's bottom edge
      // IS the divider (the top edge of the buy bar). Whatever the theme's CSS does between the two, this measures
      // the real strip and cancels it with a negative bottom margin on the scroll area. The margin is accumulated
      // (previous fix + what is still measured), so it settles at the exact value and never oscillates. The
      // freed space goes to the scroll area on the next _measureScrollArea, so the sheet keeps its height.
      _closeScrollGap() {
        const scroller = this._scroller;
        const bar = this.modalContent && this.modalContent.querySelector('.quick-add-sticky-bar');
        if (!scroller || !bar || !this._mobileQuery.matches) return false;
        const gap = bar.getBoundingClientRect().top - scroller.getBoundingClientRect().bottom;
        if (Math.abs(gap) < 0.5) return false;
        const next = Math.max(0, Math.min(60, Math.round((this._gapFix + gap) * 100) / 100));
        if (next === this._gapFix) return false;
        this._gapFix = next;
        scroller.style.setProperty('--quick-add-gap-fix', `${next}px`);
        return true;
      }

      // 0..1 = how much real content is cut off at the bottom edge RIGHT NOW. It is measured from where things
      // are actually drawn (rects), not from scroll numbers, so it already includes the rubber-band pull, the
      // lift of the sheet and a half-settled Safari bar. That is what keeps the fade honest in every bar state:
      // if any content is cut at the divider there is a fade, and it melts away by itself as the last row comes
      // into view. The trailing margin under the last row does not count, only real content does.
      // Writes to the DOM only when the value changed.
      _updateScrollFade() {
        const scroller = this._scroller;
        const inner = this._inner;
        let progress = 0;
        if (scroller && inner && this._mobileQuery.matches) {
          const hidden = inner.getBoundingClientRect().bottom - scroller.getBoundingClientRect().bottom;
          if (hidden > 0.5) progress = Math.min(1, hidden / this._fadeRange);
        }
        // quantise to 2 decimals: enough for a smooth melt, far fewer style writes while scrolling
        progress = Math.round(progress * 100) / 100;
        if (progress === this._fadeProgress) return;
        this._fadeProgress = progress;
        // Written on the scroll area itself (the only thing that reads it), so nothing else restyles.
        if (scroller) scroller.style.setProperty('--quick-add-fade-progress', progress);
      }

      // --- Rubber band: endless pulling, heavy resistance, and only the CONTENT moves ---
      //
      // Only .quick-add-scroll-inner is transformed. The scroll box, its divider and the sheet stay
      // perfectly still. The visual offset IS the state (_osY). Each finger delta is scaled by the local slope
      //
      //   slope = stiff * (1 - |offset| / limit) ^ power
      //
      // so the first pixels move almost freely (a little give), then the resistance ramps up fast, and close to
      // the limit extra dragging moves the content by a fraction of a pixel. Pulling back is damped by the same
      // factor, so the motion is always a smooth curve.
      //
      // MULTI-TOUCH: the pull is driven by ONE finger, tracked by its touch identifier (never by index).
      // Extra fingers are ignored. When the driving finger lifts while another is still down, the pull is
      // handed over to that finger WITHOUT releasing and without a jump (its Y is re-based). While a pull is
      // in progress every touchmove is cancelled, so the browser's own unlimited rubber band can never take over.
      //
      // RELEASE: a critically damped spring drives the content back to 0 (see _osSpringBack).
      _osFind(list, id) {
        if (id === null) return null;
        for (let i = 0; i < list.length; i++) {
          if (list[i].identifier === id) return list[i];
        }
        return null;
      }
      // First touch in the list that started inside the scroll area
      _osPick(list) {
        const s = this._scroller;
        if (!s) return null;
        for (let i = 0; i < list.length; i++) {
          const t = list[i];
          if (t.target && s.contains(t.target)) return t;
        }
        return null;
      }
      _osAdopt(touch) {
        this._osTouchId = touch.identifier;
        this._osLastY = touch.clientY;
      }
      _osBlock(e) {
        if (e.cancelable) e.preventDefault();
      }
      _osApply(y) {
        if (!this._inner) return;
        this._inner.style.transform = y ? `translate3d(0, ${y.toFixed(2)}px, 0)` : 'translate3d(0, 0, 0)';
        // The pull moves content past the bottom edge without any scroll event, so the fade follows it here.
        this._updateScrollFade();
      }
      _osStopSpring() {
        if (this._osSpringRaf) {
          window.cancelAnimationFrame(this._osSpringRaf);
          this._osSpringRaf = 0;
        }
      }
      _osStart(e) {
        if (!this._scroller || !e.touches || !e.touches.length) return;
        // Another finger landing while one is already driving the pull: ignore it completely.
        if (this._osTouchId !== null && this._osFind(e.touches, this._osTouchId)) return;
        // A stretch left over from a touchend we never saw (no finger, no spring): drop it.
        if (this._osTouchId === null && this._osY !== 0 && !this._osSpringRaf) this._osReset(false);
        const t = e.changedTouches && e.changedTouches[0] ? e.changedTouches[0] : e.touches[0];
        this._osAdopt(t);
        // Grabbing the content while it springs back: freeze it where it is (_osY is the live value) and carry on.
        this._osStopSpring();
      }
      _osMove(e) {
        const s = this._scroller;
        if (!s || !e.touches || !e.touches.length) return;
        let t = this._osFind(e.touches, this._osTouchId);
        if (!t) {
          // The driving finger is gone and we did not get to hand over in touchend: hand over now, no jump.
          t = this._osPick(e.touches);
          if (!t) return;
          this._osAdopt(t);
          if (this._osY !== 0) this._osBlock(e);
          return;
        }
        const y = t.clientY;
        const dy = y - this._osLastY;
        this._osLastY = y;
        const atTop = s.scrollTop <= 0;
        const atBottom = s.scrollTop + s.clientHeight >= s.scrollHeight - 1;
        const stretched = this._osY !== 0;
        if (!dy) {
          // Some other finger moved: keep the browser out while we hold a stretch
          if (stretched) this._osBlock(e);
          return;
        }
        // dy > 0 = finger moving down = content pulled down (past the TOP edge)
        // dy < 0 = finger moving up   = content pulled up   (past the BOTTOM edge)
        if (!stretched) {
          const pullingOut = (atTop && dy > 0) || (atBottom && dy < 0);
          if (!pullingOut) return; // normal native scrolling, don't interfere
        }
        this._osBlock(e);
        if (!this._osActive) {
          this._osActive = true;
          this._osStopSpring();
          if (this._inner) this._inner.style.willChange = 'transform'; // only while actually pulling
        }
        const limit = this._osLimit;
        const p = Math.min(Math.abs(this._osY) / limit, 1);
        const slope = this._osStiff * Math.pow(1 - p, this._osPower);
        let next = this._osY + dy * slope;
        // Crossed back through zero: hand control back to native scrolling.
        if ((this._osY > 0 && next <= 0) || (this._osY < 0 && next >= 0)) {
          this._osReset(false);
          this._osUnlockNative();
          return;
        }
        // Hard ceiling just under the asymptote, so it can never travel further than _osLimit.
        next = Math.max(-limit * 0.99, Math.min(limit * 0.99, next));
        this._osY = next;
        this._osQueueWrite();
      }
      _osQueueWrite() {
        if (this._osPending) return;
        this._osPending = true;
        this._osRaf = window.requestAnimationFrame(() => {
          this._osPending = false;
          this._osRaf = 0;
          this._osApply(this._osY);
        });
      }
      _osEnd(e) {
        const list = e && e.touches ? e.touches : [];
        if (list.length) {
          // Some other finger lifted: nothing to do, the driver is still down.
          if (this._osTouchId !== null && this._osFind(list, this._osTouchId)) return;
          // The DRIVING finger lifted but another one is still down: hand over, do NOT release the pull.
          const next = this._osPick(list);
          if (next) {
            this._osAdopt(next);
            return;
          }
        }
        // Last finger up (or touch cancelled): release.
        this._osTouchId = null;
        this._osReset(true);
        this._osUnlockNative();
      }
      _osReset(animate) {
        const inner = this._inner;
        this._osActive = false;
        if (this._osRaf) {
          window.cancelAnimationFrame(this._osRaf);
          this._osRaf = 0;
          this._osPending = false;
        }
        this._osStopSpring();
        if (!inner || this._osY === 0) {
          this._osY = 0;
          if (inner) inner.style.willChange = '';
          return;
        }
        if (animate && !(this._reduceMotion && this._reduceMotion.matches)) {
          this._osSpringBack();
          return;
        }
        this._osY = 0;
        this._osApply(0);
        inner.style.willChange = '';
      }
      // Critically damped spring, starting at rest: x(t) = x0 * (1 + w t) * e^(-w t)
      // No overshoot, no snap: it eases out of the stretch, moves decisively, then settles softly (~0.5 s).
      // _osY holds the live value on every frame, so a new touch can interrupt it at any moment.
      _osSpringBack() {
        const x0 = this._osY;
        const w = this._osOmega;
        const t0 = window.performance.now();
        if (this._inner) this._inner.style.willChange = 'transform';
        const step = (now) => {
          const t = Math.max(0, (now - t0) / 1000);
          const x = x0 * (1 + w * t) * Math.exp(-w * t);
          if (!this._inner || Math.abs(x) < 0.1 || t > 1.5) {
            this._osSpringRaf = 0;
            this._osY = 0;
            if (this._inner) {
              this._osApply(0);
              this._inner.style.willChange = '';
            }
            return;
          }
          this._osY = x;
          this._osApply(x);
          this._osSpringRaf = window.requestAnimationFrame(step);
        };
        this._osSpringRaf = window.requestAnimationFrame(step);
      }

      // Safety net for the browser's OWN rubber band (iOS reports scrollTop < 0 or > max while it bounces).
      // With the touch handling above it should never engage, but if it ever does while a finger is down, it is
      // switched off before it can run away: the scroller is made overflow:hidden until the gesture ends or the
      // pull crosses back through zero. Momentum bounces (no finger down) are left alone.
      _osGuardNative() {
        const s = this._scroller;
        if (!s || this._osNativeLocked || this._osTouchId === null) return;
        const max = s.scrollHeight - s.clientHeight;
        const over = s.scrollTop < 0 ? -s.scrollTop : s.scrollTop > max ? s.scrollTop - max : 0;
        if (over < 2) return;
        this._osNativeLocked = true;
        s.style.overflowY = 'hidden';
      }
      _osUnlockNative() {
        if (!this._osNativeLocked) return;
        this._osNativeLocked = false;
        if (this._scroller) this._scroller.style.overflowY = '';
      }

      // --- Shop Pay instalments line ("Pay in 3 interest-free instalments...") ---
      //
      // Shopify fills <shopify-payment-terms> a split second after the modal opens, so the header used
      // to grow when it arrived. Now:
      //   - first open on a device: hold the slide (max _termsWaitMs) until it has rendered, measure it,
      //     and reserve exactly that. If it's slow, the CSS default (1lh = one line) applies.
      //   - every open after that: the remembered height is reserved before the modal even shows.
      // Heights are stored per viewport width (a narrow phone may wrap to 2 lines).
      _termsStorageKey() {
        return `quickAdd:termsH:${Math.round(window.innerWidth)}`;
      }
      _getTermsHeight() {
        const key = this._termsStorageKey();
        if (this._termsHeights[key]) return this._termsHeights[key];
        try {
          const stored = parseFloat(window.localStorage.getItem(key));
          if (stored >= 4 && stored <= 80) {
            this._termsHeights[key] = stored;
            return stored;
          }
        } catch (e) {
          // storage blocked (private mode etc.): fine, we just re-learn each session
        }
        return 0;
      }
      _saveTermsHeight(height) {
        const key = this._termsStorageKey();
        this._termsHeights[key] = height;
        try {
          window.localStorage.setItem(key, String(height));
        } catch (e) {
          // ignore
        }
      }
      // Content height of the line WITHOUT the reservation. Read + restore happen in one task.
      _readTermsHeight(terms) {
        const inline = terms.style.minHeight;
        terms.style.minHeight = '0px';
        const height = parseFloat(window.getComputedStyle(terms).height);
        terms.style.minHeight = inline;
        return height;
      }
      // Resolves right away unless this is a cold open; otherwise resolves once the line has rendered
      // (stable across two reads) or after _termsWaitMs, whichever comes first.
      _waitForPaymentTerms() {
        if (!this._mobileQuery.matches || !this._termsWaitMs || this._termsGaveUp) return Promise.resolve();
        if (this._getTermsHeight()) return Promise.resolve(); // already known: reserved up front
        const terms = this.modalContent && this.modalContent.querySelector('shopify-payment-terms');
        if (!terms) return Promise.resolve();
        return new Promise((resolve) => {
          const startedAt = window.performance.now();
          let last = -1;
          const check = () => {
            const height = this._readTermsHeight(terms);
            if (height >= 4 && height <= 80 && Math.abs(height - last) < 0.5) {
              const rounded = Math.round(height * 100) / 100;
              this._saveTermsHeight(rounded);
              terms.style.setProperty('--quick-add-terms-h', `${rounded}px`);
              resolve();
              return;
            }
            if (window.performance.now() - startedAt > this._termsWaitMs) {
              this._termsGaveUp = true; // don't make every open wait for something that isn't coming
              resolve();
              return;
            }
            last = height;
            window.setTimeout(check, 50);
          };
          check();
        });
      }
      // Runs on the detached product element: pre-sets the reserved height (mobile only).
      reserveTermsSpace(productElement) {
        if (!this._mobileQuery.matches) return;
        const terms = productElement.querySelector('shopify-payment-terms');
        const height = terms && this._getTermsHeight();
        if (height) terms.style.setProperty('--quick-add-terms-h', `${height}px`);
      }
      // Measures the real height once the line has rendered and remembers it.
      _watchPaymentTerms() {
        this._stopWatchingPaymentTerms();
        if (!this._mobileQuery.matches || !this.modalContent) return;
        const terms = this.modalContent.querySelector('shopify-payment-terms');
        if (!terms) return;
        let last = -1;
        let stable = 0;
        let tries = 0;
        const tick = () => {
          this._termsTimer = 0;
          if (!terms.isConnected || !this.hasAttribute('open') || ++tries > 40) return;
          const height = this._readTermsHeight(terms);
          if (height >= 4 && Math.abs(height - last) < 0.5) {
            // Same value twice in a row = it has finished rendering.
            if (++stable >= 2) {
              const rounded = Math.round(height * 100) / 100;
              if (rounded <= 80 && Math.abs(rounded - (this._getTermsHeight() || 0)) > 0.5) {
                this._saveTermsHeight(rounded);
              }
              return;
            }
          } else {
            stable = 0;
          }
          last = height;
          this._termsTimer = window.setTimeout(tick, 120);
        };
        this._termsTimer = window.setTimeout(tick, 120);
      }
      _stopWatchingPaymentTerms() {
        if (this._termsTimer) {
          window.clearTimeout(this._termsTimer);
          this._termsTimer = 0;
        }
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
        this.wrapScrollArea(productElement); // works on the final DOM
        this.wrapStickyBar(productElement); // buy buttons stay in the form, just grouped + sticky
        this.reserveBottomGap(productElement); // remembered bottom space, so the first frame is already right
        this.reserveTermsSpace(productElement); // keep last: header space is reserved before injection
      }
      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }
      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();
        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();
        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }
      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;
        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });
        productElement.dataset.originalSection = sectionId;
      }
      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;
        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }
      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;
        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;
        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';
        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }
        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}